"use client";

import { businessFiltersAtom } from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { SaveSearchAuthDialog } from "@/components/auth/dialogs/save-search-auth-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Icon } from "@/components/ui/icon";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useErrorFormatter } from "@/hooks/use-error-formatter";
import { api } from "@/lib/api-client";
import type { SearchData } from "@/lib/types";
import { useAtom } from "jotai";
import { useTranslations } from "next-intl";
import { useRouter, useSearchParams } from "next/navigation";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";

export function SaveFiltersButton({
  getSearchData,
}: {
  getSearchData: () => Promise<SearchData | null>;
}) {
  const t = useTranslations();
  const { user } = useCurrentUser();
  const searchParams = useSearchParams();
  const router = useRouter();

  const isUpdate = searchParams.has("searchId");

  const createSavedSearchMutation = api.searches.saved.create.useMutation();
  const updateSavedSearchMutation = api.searches.saved.update.useMutation();

  const { displayErrorToast } = useErrorFormatter();

  const [filters, setFilters] = useAtom(businessFiltersAtom);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const pendingSearchDataRef = useRef<SearchData | null>(null);

  const hasBusinessTypeFilters = useMemo(() => {
    return filters.businessTypeIds.length > 0;
  }, [filters]);

  const hasFinancialsFilters = useMemo(() => {
    return (
      [
        filters.priceRange,
        filters.revenueRange,
        filters.profitRange,
        filters.sdeRange,
        filters.sdeMultipleRange,
        filters.revenueMultipleRange,
        filters.downPaymentRange,
      ].some((range) => range.min !== null || range.max !== null) ||
      !filters.includeMissingMultiples
    );
  }, [filters]);

  const hasDetailsFilters = useMemo(() => {
    return (
      [
        filters.establishmentAgeRange,
        filters.employeeCountRange,
        filters.ownerWorkedHoursRange,
      ].some((range) => range.min !== null || range.max !== null) ||
      filters.propertyIncluded
    );
  }, [filters]);

  const hasFinancingFilters = useMemo(() => {
    return (
      filters.financingAvailable ||
      filters.sbaPrequalified ||
      filters.visaQualified
    );
  }, [filters]);

  const hasAnyFilters = useMemo(() => {
    return (
      hasBusinessTypeFilters ||
      hasFinancialsFilters ||
      hasDetailsFilters ||
      hasFinancingFilters
    );
  }, [
    hasBusinessTypeFilters,
    hasFinancialsFilters,
    hasDetailsFilters,
    hasFinancingFilters,
  ]);

  async function handleSaveClick() {
    const searchData = await getSearchData();
    if (!searchData) {
      toast.error(t("errors.generic"));
      return;
    }

    try {
      await createSavedSearchMutation.mutateAsync(searchData);
      router.push("/my-searches");
    } catch (err) {
      displayErrorToast(err);
    }
  }

  async function handleUpdateClick() {
    const searchData = await getSearchData();
    if (!searchData) {
      toast.error(t("errors.generic"));
      return;
    }

    const searchId = searchParams.get("searchId");
    if (!searchId) {
      return;
    }

    try {
      await updateSavedSearchMutation.mutateAsync({
        id: Number.parseInt(searchId),
        ...searchData,
      });

      router.push("/my-searches");
    } catch (err) {
      displayErrorToast(err);
    }
  }

  async function handleButtonClick() {
    if (isUpdate) {
      await handleUpdateClick();
    } else if (!user) {
      const searchData = await getSearchData();
      pendingSearchDataRef.current = searchData;
      setShowAuthDialog(true);
    } else {
      await handleSaveClick();
    }
  }

  async function handleAuthenticatedSave() {
    setShowAuthDialog(false);
    const searchData = pendingSearchDataRef.current;
    pendingSearchDataRef.current = null;

    if (!searchData) {
      toast.error(t("errors.generic"));
      return;
    }

    try {
      await createSavedSearchMutation.mutateAsync(searchData);
      router.push("/my-searches");
    } catch (err) {
      displayErrorToast(err);
    }
  }

  function clearBusinessTypeFilters() {
    setFilters((prev) => ({
      ...prev,
      businessTypeIds: [],
    }));
  }

  function clearFinancialsFilters() {
    setFilters((prev) => ({
      ...prev,
      priceRange: { min: null, max: null },
      revenueRange: { min: null, max: null },
      profitRange: { min: null, max: null },
      sdeRange: { min: null, max: null },
      sdeMultipleRange: { min: null, max: null },
      revenueMultipleRange: { min: null, max: null },
      downPaymentRange: { min: null, max: null },
      includeMissingMultiples: true,
    }));
  }

  function clearDetailsFilters() {
    setFilters((prev) => ({
      ...prev,
      establishmentAgeRange: { min: null, max: null },
      employeeCountRange: { min: null, max: null },
      ownerWorkedHoursRange: { min: null, max: null },
      propertyIncluded: null,
    }));
  }

  function clearFinancingFilters() {
    setFilters((prev) => ({
      ...prev,
      financingAvailable: false,
      sbaPrequalified: false,
      visaQualified: false,
    }));
  }

  function clearAllFilters() {
    clearBusinessTypeFilters();
    clearFinancialsFilters();
    clearDetailsFilters();
    clearFinancingFilters();
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        variant={isUpdate ? "default" : "secondary"}
        className="h-11"
        disabled={
          createSavedSearchMutation.isPending ||
          updateSavedSearchMutation.isPending
        }
        onClick={handleButtonClick}
      >
        <Icon icon="SearchCheck" />
        {t(
          isUpdate
            ? "saas.search.filters.update"
            : "saas.search.filters.save",
        )}
      </Button>

      {(hasAnyFilters || isUpdate) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="h-11 w-11 px-0"
              disabled={updateSavedSearchMutation.isPending}
            >
              <Icon icon="DotsHorizontal" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent>
            {user && isUpdate && (
              <DropdownMenuItem onClick={handleSaveClick}>
                {t("saas.search.filters.more.saveAs")}
              </DropdownMenuItem>
            )}

            <DropdownMenuItem onClick={clearAllFilters}>
              {t("saas.search.filters.more.clear")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <SaveSearchAuthDialog
        open={showAuthDialog}
        onOpenChange={setShowAuthDialog}
        onAuthenticated={handleAuthenticatedSave}
      />
    </div>
  );
}

SaveFiltersButton.displayName = "SaveFiltersButton";
